import { useEffect, useRef, type PointerEvent, type ReactNode, type RefObject } from 'react';
import { ChevronLeft, ChevronRight, X } from '../lib/icons.tsx';
import Modal from './Modal.tsx';

/**
 * The magnifier shell, shared by the asset viewer and the slide viewer.
 *
 * Those two files carried about 120 lines of near-verbatim copy between them
 * — the same backdrop, the same capture-phase key handling, the same ‹ ›
 * group with its `pos+1 / n` readout, the same pointer-drag pan, the same
 * ResizeObserver box measure — and `AssetPreviewModal` said so in a comment
 * ("both copied from SlidePreviewModal").
 *
 * What is NOT shared, and must not be: the body and the zoom model. One is a
 * bitmap in an `<img>` whose fit must never upscale past 100%, because past
 * that it is only bigger pixels; the other is a live `<iframe>` of a slide
 * written in `cqw`, which genuinely re-lays-out at any size and so gets a
 * continuous zoom range. That difference is the feature, not an inconsistency.
 */

type ZoomModalProps = {
  /** Labels the dialog. There is no visible `modal__title--sm` heading row. */
  ariaLabel: string;
  /** The heading text, left of everything else in the bar. */
  heading: ReactNode;
  /** Optional facts beside the heading — dimensions, file size. */
  facts?: ReactNode;
  /** The viewer's own controls (zoom buttons, a fit toggle). */
  controls?: ReactNode;
  /** Prev/next, when there is a list to walk. */
  nav?: ReactNode;
  /** Extra class on the panel, e.g. `modal--asset-zoom`. */
  className?: string;
  onClose: () => void;
  /** Focused on mount, and where focus is parked while the viewer is open.
      No `| null` in the parameter: that is the React 19 shape, and on the 18
      types this app pins `useRef<HTMLButtonElement>(null)` already gives
      `RefObject<HTMLButtonElement>` — which is also the only thing `ref` on a
      button will take. */
  closeRef?: RefObject<HTMLButtonElement>;
  children?: ReactNode;
};

export function ZoomModal({
  ariaLabel,
  heading,
  facts,
  controls,
  nav,
  className,
  onClose,
  closeRef,
  children,
}: ZoomModalProps) {
  return (
    <Modal
      ariaLabel={ariaLabel}
      onClose={onClose}
      className={'modal--zoom' + (className ? ' ' + className : '')}
      backdropClassName="modal-backdrop--zoom"
      // Rendered INSIDE picking rooms whose own backdrop closes them on
      // mousedown, and whose own Escape handler would fire first. Both are
      // suppressed by `nested`; without it, dismissing the picture dismisses
      // the room behind it along with its search, tab and every ticked asset.
      nested
      // The viewer's own bar IS the header, so Modal renders none and the
      // children are the whole panel.
      titleAs="h3"
    >
      <header className="modal__header modal__header--zoom">
        <h3 className="modal__title modal__title--sm">{heading}</h3>
        {facts}
        <span className="zoom__spacer" />
        {nav}
        {controls}
        <button type="button" className="iconbtn" ref={closeRef} onClick={onClose} title="Close" aria-label="Close">
          <X />
        </button>
      </header>
      {children}
    </Modal>
  );
}

/**
 * The ‹ N / M › group. Identical in both viewers down to the `aria-live`;
 * only the noun differs, and it is what the labels are built from.
 */
export function ZoomNav({
  noun,
  plural,
  pos,
  total,
  onStep,
}: {
  noun: string;
  plural: string;
  pos: number;
  total: number;
  onStep: (delta: number) => void;
}) {
  return (
    <div className="zoom__controls" role="group" aria-label={plural}>
      <button
        type="button"
        className="iconbtn"
        onClick={() => onStep(-1)}
        disabled={pos <= 0}
        title={`Previous ${noun} (←)`}
        aria-label={`Previous ${noun}`}
      >
        <ChevronLeft />
      </button>
      <span className="zoom__count" aria-live="polite">
        {pos + 1} / {total}
      </span>
      <button
        type="button"
        className="iconbtn"
        onClick={() => onStep(1)}
        disabled={pos >= total - 1}
        title={`Next ${noun} (→)`}
        aria-label={`Next ${noun}`}
      >
        <ChevronRight />
      </button>
    </div>
  );
}

/**
 * Arrow keys walk the list, on the CAPTURE phase, stopping propagation.
 *
 * Two reasons, and both are load-bearing. Capture runs before any host's
 * bubble-phase handler, so a viewer raised from a picking room does not take
 * the room down with it. And the filmstrip behind the editor moves the EDITED
 * slide on arrows, so without the stop, browsing in here would quietly
 * re-point the editor at whatever you scrolled past.
 *
 * Escape is NOT handled here — `ZoomModal` passes `nested` to Modal, which
 * owns it on the same phase for the same reason.
 *
 * @param extra keys this viewer handles itself (zoom in/out/fit). Return true
 *              to say the key was consumed.
 */
export function useZoomKeys(canNav: boolean, onStep: (delta: number) => void, extra?: (e: KeyboardEvent) => boolean) {
  const ref = useRef({ canNav, onStep, extra });
  ref.current = { canNav, onStep, extra };
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const { canNav: nav, onStep: step, extra: more } = ref.current;
      if (more?.(e)) return;
      if (nav && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        e.stopPropagation();
        step(e.key === 'ArrowRight' ? 1 : -1);
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);
}

/**
 * Drag to pan a scroller, for when the content overflows it.
 *
 * `moved` exists for the asset viewer: a pan ends in a click, and that click
 * toggles back to fit, so dragging to look at a corner would snap you out of
 * actual size the moment you let go. Anything past a few pixels counts as a
 * drag rather than a click. The slide viewer ignores it.
 */
export function useDragPan(scrollerRef: RefObject<HTMLElement | null>, enabled = true) {
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const moved = useRef(false);

  return {
    moved,
    handlers: {
      onPointerDown(e: PointerEvent<HTMLDivElement>) {
        const el = scrollerRef.current;
        if (!el || !enabled) return;
        drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
        moved.current = false;
        e.currentTarget.setPointerCapture(e.pointerId);
      },
      onPointerMove(e: PointerEvent<HTMLDivElement>) {
        const el = scrollerRef.current;
        if (!drag.current || !el) return;
        const dx = e.clientX - drag.current.x;
        const dy = e.clientY - drag.current.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved.current = true;
        el.scrollLeft = drag.current.left - dx;
        el.scrollTop = drag.current.top - dy;
      },
      onPointerUp(e: PointerEvent<HTMLDivElement>) {
        drag.current = null;
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* pointer already gone */
        }
      },
    },
  };
}
